import { trimStringsFromParams, queryString } from './strings';
import { MultipartUrl, ParamsUrl } from './types';
import { countNumberOfParams } from './countNumberOfParams';
import { fetchRefreshToken } from './checkTokenValidity';
import axios, {
  AxiosError,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { BASE_URL } from '@jobcost-monorepo/crud';
import {
  getTokenCookies,
  addTokenCookies,
  removeTokenCookies,
  ROUTES,
} from '@jobcost-monorepo/utils';

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const instance = axios.create({
  baseURL: BASE_URL,
  // Senza timeout una chiamata appesa (es. il login a Gestify) lascia
  // l'interfaccia in caricamento a tempo indefinito.
  timeout: 60_000,
  headers: {
    'Content-Type': 'application/json',
  } as AxiosRequestHeaders,
});

instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const accessToken = getTokenCookies().accessToken;
  if (config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  } else {
    config.headers = {
      Authorization: `Bearer ${accessToken}`,
    } as AxiosRequestHeaders;
  }
  return config;
});

instance.interceptors.response.use(
  (res: AxiosResponse) => {
    return res;
  },
  async (err: AxiosError) => {
    const originalConfig = err.config as RetryableRequestConfig;

    if (err.response) {
      if (err.response.status === 401 && !originalConfig._retry) {
        originalConfig._retry = true;

        try {
          const { refreshToken } = getTokenCookies();
          if (refreshToken) {
            const result = await fetchRefreshToken(refreshToken);
            const { token, refresh } = result || {};
            if (token && refresh) {
              addTokenCookies({
                accessToken: token,
                refreshToken: refresh,
              });
              return instance.request(originalConfig);
            } else {
              removeTokenCookies();
              window.location.assign(ROUTES.SIGN_IN);
              return Promise.reject(err);
            }
          }
          // @ts-ignore
        } catch (e: never) {
          if (e.response && e.response?.data) {
            return Promise.reject(e.response?.data);
          }

          return Promise.reject(e);
        }
      }
    }

    return Promise.reject(err);
  },
);

const axiosGet = async <AxiosResponse>({
  url,
  params,
}: ParamsUrl): Promise<AxiosResponse> => {
  const query = queryString(params);
  try {
    if (countNumberOfParams(params) > 200)
      return Promise.reject(
        Object.assign(
          {},
          {
            status: 'error.status.limit_selected_fixed_number',
          },
        ),
      );
    return instance.get(`${url}${query || ''}`);
  } catch (error: any) {
    return error;
  }
};

const axiosPost = async <AxiosResponse>({
  url,
  params,
}: ParamsUrl): Promise<AxiosResponse> => {
  try {
    return instance.post(url, trimStringsFromParams(params));
  } catch (error: any) {
    return error;
  }
};

const axiosDelete = async <AxiosResponse>({
  url,
  params,
}: ParamsUrl): Promise<AxiosResponse> => {
  try {
    return instance.delete(url, { data: trimStringsFromParams(params) });
  } catch (error: any) {
    return error;
  }
};

const axiosMultipart = async <AxiosResponse>({
  url,
  formData,
}: MultipartUrl): Promise<AxiosResponse> => {
  try {
    const config = {
      headers: {
        // Senza boundary: lo genera axios (nel browser, il browser stesso)
        // serializzando il FormData, sovrascrivendo questo header. Va però
        // dichiarato, altrimenti resta il `Content-Type: application/json`
        // di default dell'istanza.
        'content-type': 'multipart/form-data',
      },
    };
    return instance.post(url, formData, config);
  } catch (error: any) {
    return error;
  }
};

/**
 * <p>Tutti i metodi della classe apiClient passano da interceptor sia come richieste che come risposte.</p>
 * <p></p>
 * <p><b>Requests</b> - Ogni richiesta verifica che l'ID di sessione sia valido per evitare che un utente abbia
 * contemporaneamente più tab aperti su aziende diverse.</p>
 * <p></p>
 * <p><b>Responses</b> - Ogni risposta che da errore 401 viene intercettata e si tenta il refresh del token.
 * In caso di esito positivo viene rifatta la richiesta in modo trasparente altrimenti vengono eliminati
 * i cookies e si viene redirezionati al login.</p>
 */
export class apiClient {
  static get = axiosGet;
  static post = axiosPost;
  static delete = axiosDelete;
  static multipart = axiosMultipart;
}
